import z from "zod";
import { dayjs } from "../dayjs/dayjs.js";
import { datesInRange, ISO_DATE_FORMAT } from "./date.js";
/**
 * Admits {@link END_OF_DAY} alongside the times a clock can show, because a
 * span running to midnight has no other way to say where it ends.
 */
export const TimeOfDaySchema = z
    .string()
    .regex(/^([01]\d|2[0-4]):([0-5]\d):00$/, "Not a time of day")
    .refine((time) => time <= "24:00:00", "Past the end of the day")
    .brand();
/**
 * The end of a day, one minute past what a clock can show. Postgres `TIME`
 * accepts it and sorts it after `23:59:59`, so a span running to midnight
 * needs no special case in storage, ordering or comparison.
 */
export const END_OF_DAY = TimeOfDaySchema.parse("24:00:00");
const MINUTES_PER_HOUR = 60;
/** Minutes since midnight, `1440` for {@link END_OF_DAY}. */
export function parseTimeOfDay(time) {
    const [hours, minutes] = time.split(":");
    return Number(hours) * MINUTES_PER_HOUR + Number(minutes);
}
/**
 * The inverse of {@link parseTimeOfDay}.
 *
 * Throws for a minute count outside the day — the parse below is the check, as
 * no such count formats into a time of day. Its argument is one this module
 * computed, so a bad one is a caller bug rather than bad data, and nothing
 * downstream could act on it.
 */
export function formatTimeOfDay(minutesSinceMidnight) {
    return TimeOfDaySchema.parse(`${formatHoursAndMinutes(minutesSinceMidnight)}:00`);
}
/**
 * `HH:MM` as a clock shows it, for labels; {@link END_OF_DAY} reads `24:00`.
 * Shares its formatting with {@link formatTimeOfDay}, so the two can never
 * disagree about how a minute count is spelt.
 */
export function formatClockTime(time) {
    return formatHoursAndMinutes(parseTimeOfDay(time));
}
function formatHoursAndMinutes(minutesSinceMidnight) {
    const hours = Math.floor(minutesSinceMidnight / MINUTES_PER_HOUR);
    const minutes = minutesSinceMidnight % MINUTES_PER_HOUR;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}
function toMinuteSpan(range) {
    return {
        from: parseTimeOfDay(range.from),
        until: parseTimeOfDay(range.until),
    };
}
function mergeSpans(spans) {
    // Sorted by start only: merging takes the later end of an overlapping pair,
    // so two ranges sharing a start give the same answer in either order.
    const sorted = [...spans].sort((left, right) => left.from - right.from);
    const merged = [];
    for (const span of sorted) {
        const previous = merged.at(-1);
        if (previous !== undefined && span.from <= previous.until) {
            previous.until = Math.max(previous.until, span.until);
            continue;
        }
        merged.push({ ...span });
    }
    return merged;
}
/**
 * The fewest ranges describing the same time, earliest first. Ranges that
 * merely touch are merged, so a caller sees whole spans rather than the
 * pieces they were built from.
 *
 * Every range must belong to the same day — the type carries no day, which is
 * what makes that the caller's job.
 */
export function mergeAdjacentTimeRanges(ranges) {
    return mergeSpans(ranges.map(toMinuteSpan)).map((span) => ({
        from: formatTimeOfDay(span.from),
        until: formatTimeOfDay(span.until),
    }));
}
/**
 * The fewest ranges describing the same time, by date then start; ranges that
 * touch or overlap on one date become one. The per-date sibling of
 * {@link mergeAdjacentTimeRanges}, which has no date to group by.
 */
export function mergeTimeRangesOnDate(ranges) {
    const byDate = new Map();
    for (const range of ranges) {
        const onDate = byDate.get(range.date) ?? [];
        onDate.push({ from: range.from, until: range.until });
        byDate.set(range.date, onDate);
    }
    return [...byDate.entries()]
        .sort(([left], [right]) => left.localeCompare(right))
        .flatMap(([date, onDate]) => mergeAdjacentTimeRanges(onDate).map((merged) => ({ date, ...merged })));
}
/** Whether `ranges` account for every minute of `target`, same day throughout. */
export function timeRangesCover(ranges, target) {
    const wanted = toMinuteSpan(target);
    return mergeSpans(ranges.map(toMinuteSpan)).some((span) => span.from <= wanted.from && span.until >= wanted.until);
}
/**
 * The longest single stretch of `target` that `ranges` account for, after
 * merging what touches, or `undefined` when they share no minute. Ties go to
 * the earliest stretch.
 *
 * The whole answer lies inside `target`: time on either side of it is not part
 * of the stretch, however far a range runs. Same day throughout.
 */
export function longestOverlap(ranges, target) {
    const wanted = toMinuteSpan(target);
    let longest;
    for (const span of mergeSpans(ranges.map(toMinuteSpan))) {
        const from = Math.max(span.from, wanted.from);
        const until = Math.min(span.until, wanted.until);
        if (until <= from) {
            continue;
        }
        if (longest === undefined || until - from > longest.until - longest.from) {
            longest = { from, until };
        }
    }
    if (longest === undefined) {
        return undefined;
    }
    return {
        from: formatTimeOfDay(longest.from),
        until: formatTimeOfDay(longest.until),
    };
}
/**
 * `ranges` with every minute of `toRemove` taken out, earliest first. Both
 * sides are merged first, so overlapping removals take out their union and
 * what remains is disjoint and never touching. A removal that merely touches
 * a range removes nothing. Same day throughout.
 */
export function subtractTimeRanges(ranges, toRemove) {
    const taken = mergeSpans(toRemove.map(toMinuteSpan));
    const remaining = [];
    for (const span of mergeSpans(ranges.map(toMinuteSpan))) {
        let cursor = span.from;
        for (const gap of taken) {
            const from = Math.max(gap.from, cursor);
            const until = Math.min(gap.until, span.until);
            if (until <= from) {
                continue;
            }
            if (from > cursor) {
                remaining.push({ from: cursor, until: from });
            }
            cursor = until;
        }
        if (cursor < span.until) {
            remaining.push({ from: cursor, until: span.until });
        }
    }
    return remaining.map((span) => ({
        from: formatTimeOfDay(span.from),
        until: formatTimeOfDay(span.until),
    }));
}
/** Whether two ranges name the same span; the type has no identity of its own. */
export function timeRangesEqual(left, right) {
    return left.from === right.from && left.until === right.until;
}
/** Whether any range shares a minute with `target`. Merely touching does not. */
export function timeRangesOverlap(ranges, target) {
    const wanted = toMinuteSpan(target);
    return ranges
        .map(toMinuteSpan)
        .some((span) => span.from < wanted.until && span.until > wanted.from);
}
/**
 * A repeating week projected onto the dates in a range.
 *
 * Deliberately has no time zone. Dates are walked in UTC, where no day is ever
 * 23 or 25 hours long, so the same clock time lands on every date — which is
 * the property the wall-clock model exists to give.
 */
export function expandWeekdaysToDates(ranges, dateRange) {
    const dated = [];
    for (const date of datesInRange(dateRange)) {
        const dayOfWeek = dayjs.utc(date, ISO_DATE_FORMAT).isoWeekday();
        for (const range of ranges) {
            if (range.dayOfWeek === dayOfWeek) {
                // Spreading the range would carry its dayOfWeek onto a dated one,
                // which has a date instead and no use for one.
                dated.push({ date, from: range.from, until: range.until });
            }
        }
    }
    return dated;
}
//# sourceMappingURL=time.js.map